import { getCookie } from '../../utils/helpers';

interface MilestoneAttributesData {
  id: string;
  milestone_attributes: Record<
    string,
    {
      async?: boolean;
      audited?: boolean;
      approvalRequired?: boolean;
    }
  >;
}

/**
 * Get authentication headers with bearer token
 */
const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

class MilestoneAttributesService {
  private static instance: MilestoneAttributesService;
  private cache: MilestoneAttributesData | null = null;
  private isLoading = false;
  private callbacks: Array<
    (data: MilestoneAttributesData | null, error?: string) => void
  > = [];

  private readonly API_URL = process.env.NEXT_PUBLIC_API_URL
    ? process.env.NEXT_PUBLIC_API_URL +
      '/product-roadmap/api/v2/templates/milestone/attributes'
    : process.env.REACT_APP_API_V2_URL
    ? `${process.env.REACT_APP_API_V2_URL}/templates/milestone/attributes`
    : process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/product-roadmap/api/v2/templates/milestone/attributes'
    : '/product-roadmap/api/v2/templates/milestone/attributes';

  private constructor() {}

  static getInstance(): MilestoneAttributesService {
    if (!MilestoneAttributesService.instance) {
      MilestoneAttributesService.instance = new MilestoneAttributesService();
    }
    return MilestoneAttributesService.instance;
  }

  /**
   * Get milestone attributes with caching
   * Returns cached data if available, otherwise fetches from API
   */
  async getMilestoneAttributes(): Promise<MilestoneAttributesData> {
    return new Promise((resolve, reject) => {
      // If data is already cached, return it immediately
      if (this.cache) {
        resolve(this.cache);
        return;
      }

      // Add callback to queue
      this.callbacks.push((data, error) => {
        if (error) {
          reject(new Error(error));
        } else if (data) {
          resolve(data);
        } else {
          reject(new Error('No data received'));
        }
      });

      // If already loading, just wait for the result
      if (this.isLoading) {
        return;
      }

      // Start loading
      this.isLoading = true;
      this.fetchMilestoneAttributes();
    });
  }

  /**
   * Force refresh the cache (useful for manual refresh or error recovery)
   */
  async refreshCache(): Promise<MilestoneAttributesData> {
    this.cache = null;
    this.isLoading = false;
    return this.getMilestoneAttributes();
  }

  /**
   * Check if attributes are cached
   */
  isCached(): boolean {
    return this.cache !== null;
  }

  /**
   * Get cached data without making API call (returns null if not cached)
   */
  getCachedData(): MilestoneAttributesData | null {
    return this.cache;
  }

  private async fetchMilestoneAttributes(): Promise<void> {
    try {
      const headers = getAuthHeaders();

      const response = await fetch(this.API_URL, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle the actual API response format
      // The API returns milestone attributes directly, but we need to wrap it
      // in the same structure as the JSON file for compatibility
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // API returns milestone attributes directly
        this.cache = {
          id: 'api-response', // Add a default ID
          milestone_attributes: data
        };
      } else if (Array.isArray(data) && data.length > 0) {
        // Fallback: if API returns array format (like JSON file)
        this.cache = data[0];
      } else {
        // Fallback: wrap the data in expected structure
        this.cache = {
          id: 'api-response',
          milestone_attributes: data || {}
        };
      }

      // Notify all waiting callbacks
      this.callbacks.forEach(callback => callback(this.cache));
      this.callbacks = [];
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch milestone attributes';

      // Notify all waiting callbacks about the error
      this.callbacks.forEach(callback => callback(null, errorMessage));
      this.callbacks = [];

      console.error('Error fetching milestone attributes:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Clear the cache (useful for testing or when data needs to be refreshed)
   */
  clearCache(): void {
    this.cache = null;
  }
}

// Export singleton instance
export const milestoneAttributesService =
  MilestoneAttributesService.getInstance();

// Export types for use in components
export type { MilestoneAttributesData };
